import { useDeferredValue, useEffect, useMemo } from 'react'

import { CoverageDashboardSkeleton } from './CoverageDashboardSkeleton'
import { CoverageListBody } from './CoverageListBody'
import { useGisSites } from '../../../hooks/useGisSites'
import {
  coverageGroupLabelFor,
  filterSites,
  groupSites,
  type SiteFilters,
} from '../../../lib/coverage-board'
import { mandalOfficerRollup } from '../../../lib/mandal-officers'
import { periodLabel } from '../../../lib/period-label'
import { useGisCoverageView } from '../../../stores/gisCoverageViewStore'
import { useGisIssueView } from '../../../stores/gisIssueViewStore'
import { PanelCard } from '../../ui/PanelCard'

import type { ReportFilters } from '../../../api/reportsApi'
import type { GisOverview, GisScope, MandalAggregate, SiteStatus } from '../../../api/types'

/** Worst-status-first: overdue + never-surveyed on top; tie-break ascending completion. */
const STATUS_RANK: Record<SiteStatus, number> = { overdue: 0, never_surveyed: 1, in_progress: 2, surveyed: 3 }

/**
 * The single mandal aggregate behind the page filter — resolved ONLY when exactly one mandal is
 * selected (its own coverage headline). With 0 or 2+ selected the headline falls back district-wide.
 */
const mandalRow = (overview: GisOverview, activeMandals: readonly string[]): MandalAggregate | null =>
  activeMandals.length === 1 ? (overview.mandals.find((m) => m.name === activeMandals[0]) ?? null) : null

interface CoverageDashboardProps {
  overview: GisOverview
  scope: GisScope
  activeMandals: readonly string[]
  onSelectSite: (id: string) => void
}

/** The card's mode-aware headline (coverage % + working-set size / officer count / report window). */
function coverageSubtitle(a: {
  listMode: 'sites' | 'officers' | 'surveys'
  coveragePct: number | null
  shown: number
  total: number
  groupBy: ReturnType<typeof useGisCoverageView.getState>['groupBy']
  officerCount: number
  scope: GisScope
}): string {
  if (a.listMode === 'sites') {
    const pct = a.coveragePct !== null ? `Coverage ${a.coveragePct}% · ` : ''
    const grouped = a.groupBy !== 'none' ? ` · Grouped by ${coverageGroupLabelFor(a.groupBy).toLowerCase()}` : ''
    return `${pct}Showing ${a.shown} of ${a.total} sites · Worst first${grouped}`
  }
  if (a.listMode === 'officers') {
    return `${a.officerCount} officer${a.officerCount === 1 ? '' : 's'} · Worst completion first`
  }
  return `Scheduled surveys — pending and missed · ${periodLabel(a.scope.period, a.scope.dateFrom, a.scope.dateTo)}`
}

/**
 * District **Coverage** board (V5 toolbar layout — the exact sibling of `issues/IssuesDashboard`).
 * One list card fills the panel; every control lives in the page-level `CoverageToolbar` and this
 * component only READS `gisCoverageViewStore` and renders the working set: worst-first sites
 * (grouped by status by default), the officer rollup, or the pending/missed Surveys report. The
 * coverage %-headline lives in the card subtitle. Filters/hover reset on unmount so no other tab
 * inherits a filtered map.
 */
export function CoverageDashboard({ overview, scope, activeMandals, onSelectSite }: CoverageDashboardProps) {
  const v = useGisCoverageView
  const statusFilter = v((s) => s.statusFilter)
  const officerFilter = v((s) => s.officerFilter)
  const query = v((s) => s.query)
  const groupBy = v((s) => s.groupBy)
  const listMode = v((s) => s.listMode)
  const setHoveredSite = useGisIssueView((s) => s.setHoveredSite)
  const deferredQuery = useDeferredValue(query)
  // Leaving the tab must not strand a filtered map or a highlighted pin.
  useEffect(
    () => () => {
      useGisCoverageView.getState().reset()
      useGisIssueView.getState().setHoveredSite(null)
    },
    [],
  )
  // Dedupes with the page-level sites query (same queryOptions factory) — pending only on cold load,
  // where `overview.sites` would otherwise render as a misleading "0 sites".
  const { data: sitesData, isPending: sitesPending } = useGisSites(scope)

  const filters: SiteFilters = {
    statuses: statusFilter,
    officers: officerFilter,
    mandal: activeMandals,
    query: deferredQuery,
  }
  const sortedSites = useMemo(() => {
    const base = filterSites(overview.sites, filters)
    return [...base].sort(
      (a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status] || a.completion - b.completion,
    )
     
  }, [overview.sites, statusFilter, officerFilter, deferredQuery, activeMandals])
  const groups = useMemo(() => groupSites(sortedSites, groupBy), [sortedSites, groupBy])
  // Officer rollup over the SAME filtered set — every toolbar filter applies to both views.
  const officerRows = useMemo(() => mandalOfficerRollup(sortedSites), [sortedSites])

  // Sites in the mandal scope alone (the "of M" denominator — shows how much the other filters cut).
  const mandalScopedCount = useMemo(
    () =>
      activeMandals.length > 0
        ? overview.sites.filter((s) => activeMandals.includes(s.mandal)).length
        : overview.sites.length,
    [overview.sites, activeMandals],
  )
  const row = mandalRow(overview, activeMandals)
  const coveragePct = row ? row.coverage : overview.summary.coverage
  const filtered = statusFilter.length > 0 || officerFilter.length > 0 || deferredQuery.trim() !== '' || activeMandals.length > 0

  if (sitesPending && sitesData === undefined) {
    return <CoverageDashboardSkeleton />
  }

  const reportFilters: ReportFilters = {
    scope,
    mandal: activeMandals.length === 1 ? activeMandals[0] : undefined,
  }
  const subtitle = coverageSubtitle({
    listMode,
    coveragePct,
    shown: sortedSites.length,
    total: mandalScopedCount,
    groupBy,
    officerCount: officerRows.length,
    scope,
  })

  return (
    <div className="grid min-h-0 flex-1 grid-rows-[minmax(0,1fr)] p-3">
      <PanelCard title="Coverage" subtitle={subtitle} fillBody className="min-h-0">
        <CoverageListBody
          mode={listMode}
          sites={sortedSites}
          groups={groups}
          groupBy={groupBy}
          officers={officerRows}
          filtered={filtered}
          reportFilters={reportFilters}
          onSelectSite={onSelectSite}
          onHover={setHoveredSite}
        />
      </PanelCard>
    </div>
  )
}
